// src/Studio/types/gradientFade.ts

import { nanoid } from 'nanoid';

// ==================== Mask Types ====================

/** Individual point in a mask path */
export interface MaskPoint {
  x: number;
  y: number;
  /** Bezier incoming control handle (relative to point) */
  cpIn?: { x: number; y: number };
  /** Bezier outgoing control handle (relative to point) */
  cpOut?: { x: number; y: number };
}

/** Selection operation for combining multiple paths */
export type MaskOperation = 'replace' | 'add' | 'subtract' | 'intersect';

/** A single path in the mask */
export interface MaskPath {
  id: string;
  points: MaskPoint[];
  closed: boolean;
  operation: MaskOperation;
}

/** Vector mask definition */
export interface VectorMask {
  enabled: boolean;
  paths: MaskPath[];
  featherPx: number;
  expandPx: number;
  invert: boolean;
}

// ==================== Gradient Types ====================

export type GradientType = 'linear' | 'radial';

/** Gradient fade definition */
export interface GradientFade {
  type: GradientType;
  strength: number;
  softness: number;
  // Linear-specific
  angle: number;
  offsetX: number;
  offsetY: number;
  // Radial-specific
  centerX: number;
  centerY: number;
  radius: number;
  // Common
  invert: boolean;
}

// ==================== Combined Opacity Settings ====================

export type OpacityMode = 'uniform' | 'gradient-fade';

/** Combined opacity settings stored on element */
export interface OpacitySettings {
  mode: OpacityMode;
  uniform: number;
  mask?: VectorMask;
  gradient?: GradientFade;
}

// ==================== Mask Edit State ====================

export type MaskTool = 'lasso' | 'pen';

export interface MaskEditState {
  elementId: string;
  tool: MaskTool;
  operation: MaskOperation;
  /** Currently drawing path (not yet closed) */
  activePath: MaskPath | null;
  /** Selected point index for pen tool editing */
  selectedPointIndex: number | null;
  /** Selected path id for editing */
  selectedPathId: string | null;
  /** Show overlay preview toggle */
  showOverlayPreview: boolean;
}

// ==================== Defaults ====================

export const DEFAULT_VECTOR_MASK: VectorMask = {
  enabled: false,
  paths: [],
  featherPx: 0,
  expandPx: 0,
  invert: false,
};

export const DEFAULT_GRADIENT_FADE: GradientFade = {
  type: 'linear',
  strength: 100,
  softness: 50,
  angle: 90,
  offsetX: 0,
  offsetY: 0,
  centerX: 50,
  centerY: 50,
  radius: 50,
  invert: false,
};

export const DEFAULT_OPACITY_SETTINGS: OpacitySettings = {
  mode: 'uniform',
  uniform: 1,
  mask: undefined,
  gradient: undefined,
};

export const DEFAULT_MASK_EDIT_STATE: Omit<MaskEditState, 'elementId'> = {
  tool: 'lasso',
  operation: 'replace',
  activePath: null,
  selectedPointIndex: null,
  selectedPathId: null,
  showOverlayPreview: false,
};

// ==================== Factory Helpers ====================

export function createMaskPath(operation: MaskOperation = 'replace'): MaskPath {
  return {
    id: nanoid(8),
    points: [],
    closed: false,
    operation,
  };
}

export function createOpacitySettings(uniform: number = 1): OpacitySettings {
  return {
    mode: 'uniform',
    uniform,
    mask: undefined,
    gradient: undefined,
  };
}
